/**
 * =============================================================================
 *  SO DO TRANG -> NHUNG GI KHUNG XEM CHINH THUC SU VE
 * =============================================================================
 *
 *  Truoc tep nay, thanh ben ve tu so do trang con khung xem chinh ve tu tai
 *  lieu pdf.js DA MO LUC DAU. Hai nguon su that. Ghep them mot tai lieu vao thi
 *  thanh ben hien 4 trang trong khi bo dem cua khung xem van ghi "/ 3" va van
 *  ve dung 3 trang cu. Tep xuat ra thi dung - tuc la man hinh dang hien mot tai
 *  lieu KHONG CON TON TAI. Do la dang hong de bi nham voi "san pham hong" nhat
 *  trong ca ung dung, vi thu nguoi dung tin la ket qua chinh la thu ho nhin.
 *
 *  Tu day: so do trang la nguon su that DUY NHAT cho bo dem trang, danh sach
 *  trang ao hoa, anh thu nho, dieu huong, va viec o trang nao ve tu doi tuong
 *  trang nao cua pdf.js.
 *
 * =============================================================================
 *  BA DIEU MOT O TRANG PHAI TU TRA LOI DUOC
 * =============================================================================
 *
 *  1. VE TU TAI LIEU NAO. Trang ghep den tu MOT TAI LIEU pdf.js KHAC. Khung
 *     xem giu mot doi tuong tai lieu cho moi nguon va tra ve `docId`; khong cho
 *     nao duoc phep gia dinh "chi co mot tai lieu".
 *
 *  2. XOAY BAO NHIEU. Do xoay hien thi = /Rotate KHAI BAO TRONG TRANG cong voi
 *     phan nguoi dung vua xoay them. Mot trang scan da la /Rotate 90 ma bam
 *     "xoay phai" phai ra 180. Cong o day, DUNG MOT LAN, roi ca kich thuoc
 *     hien thi lan phep doi he toa do cua cac lop phu deu doc ra tu no.
 *
 *  3. TO BAO NHIEU KHI LA TRANG TRANG. Trang trang khong co doi tuong trang de
 *     hoi, nen kich thuoc cua no di theo quy tac trong pages/plan.ts - DUNG quy
 *     tac ma duong xuat dung. Neu khong, trang trang tren man hinh se to hoac
 *     nho hon trang trang trong tep.
 */
import {
  ORIGINAL_DOC_ID,
  normalizeAngle,
  resolveBlankSizes,
  type PagePlan,
  type PageSlot,
} from '../core/pages/plan';

/** Sieu du lieu mot trang cua mot tai lieu nguon, doc tu DocumentInfo. */
export interface SourcePageMeta {
  widthPt: number;
  heightPt: number;
  /** /Rotate khai bao trong chinh trang do. */
  rotation: 0 | 90 | 180 | 270;
  hasTextLayer: boolean;
}

/** Mot tai lieu nguon dang mo cho viec HIEN THI. */
export interface PlanDocumentMeta {
  pages: readonly SourcePageMeta[];
}

/**
 * Mot o trang da phan giai xong: du de ve ma khong phai hoi them ai.
 */
export interface ViewSlot {
  /** Vi tri trong so do - cung la so trang hien cho nguoi dung (+1). */
  slotIndex: number;
  docId: string;
  /** Chi so trang trong tai lieu NGUON. null = trang trang. */
  sourcePageIndex: number | null;
  /**
   * Chi so trang trong TAI LIEU GOC, hoac null khi o nay khong den tu tai lieu goc.
   *
   * Tach rieng khoi `sourcePageIndex` vi day la khong gian chi so ma ma khoi
   * van ban ("p3-b7") va `Annotation.pageIndex` song trong do. Mot trang ghep
   * co sourcePageIndex = 0 cua tai lieu cua no, nhung no KHONG phai trang 0 cua
   * ban goc, va sua chu / chu thich khong duoc phep nham hai thu do voi nhau.
   */
  originalPageIndex: number | null;
  /** Kich thuoc trang nguon, CHUA xoay. */
  widthPt: number;
  heightPt: number;
  /** Do xoay HIEN THI: /Rotate cua trang CONG phan xoay them cua so do. */
  rotation: 0 | 90 | 180 | 270;
  /** Phan xoay them do nguoi dung yeu cau - de hien nhan tren anh thu nho. */
  rotationDelta: number;
  hasTextLayer: boolean;
  isBlank: boolean;
  /**
   * true khi tai lieu nguon cua o nay chua mo xong.
   *
   * Kich thuoc luc do chi la phong doan (theo trang lang gieng), nen khung xem
   * ve o giu cho chu KHONG duoc ve trang lang gieng vao day: mot trang cu nam
   * dung cho trang moi la loi im lang, nguoi dung khong the biet minh dang nhin
   * nham.
   */
  pending: boolean;
}

const FALLBACK_ROTATION: 0 | 90 | 180 | 270 = 0;

function asRotation(angle: number): 0 | 90 | 180 | 270 {
  const n = normalizeAngle(angle);
  return n === 90 || n === 180 || n === 270 ? n : 0;
}

function metaFor(
  slot: PageSlot,
  documents: ReadonlyMap<string, PlanDocumentMeta>,
): SourcePageMeta | null {
  if (slot.sourcePageIndex === null) return null;
  const doc = documents.get(slot.docId);
  if (!doc) return null;
  return doc.pages[slot.sourcePageIndex] ?? null;
}

/**
 * Phan giai ca so do thanh danh sach o trang ve duoc.
 *
 * Ham THUAN TUY. Do la dieu kien de no kiem chung duoc bang test ma khong can
 * pdf.js, va cung la ly do bo dem trang khong the lech khoi danh sach trang: ca
 * hai deu chi la mot phep doc tren ket qua cua ham nay.
 */
export function resolvePlanView(
  plan: PagePlan,
  documents: ReadonlyMap<string, PlanDocumentMeta>,
): ViewSlot[] {
  // Kich thuoc cho o trang trang, theo DUNG quy tac cua duong xuat.
  const blankSizes = resolveBlankSizes(plan, (i) => {
    const slot = plan[i];
    if (!slot) return null;
    const meta = metaFor(slot, documents);
    return meta ? { widthPt: meta.widthPt, heightPt: meta.heightPt } : null;
  });

  return plan.map((slot, slotIndex) => {
    const isBlank = slot.sourcePageIndex === null;
    const meta = metaFor(slot, documents);
    const blank = blankSizes[slotIndex] ?? { widthPt: 595.28, heightPt: 841.89 };

    // Kich thuoc: cua chinh trang neu co; neu chua co (trang trang, hoac tai
    // lieu nguon chua mo xong) thi theo quy tac trang lang gieng.
    const widthPt = meta ? meta.widthPt : blank.widthPt;
    const heightPt = meta ? meta.heightPt : blank.heightPt;

    // DIEM MAU CHOT: cong, khong dat de. Xem ghi chu (2) o dau tep.
    const declared = meta ? meta.rotation : FALLBACK_ROTATION;

    return {
      slotIndex,
      docId: slot.docId,
      sourcePageIndex: slot.sourcePageIndex,
      originalPageIndex:
        slot.docId === ORIGINAL_DOC_ID ? slot.sourcePageIndex : null,
      widthPt,
      heightPt,
      rotation: asRotation(declared + slot.rotationDelta),
      rotationDelta: slot.rotationDelta,
      hasTextLayer: meta ? meta.hasTextLayer : false,
      isBlank,
      pending: !isBlank && meta === null,
    } satisfies ViewSlot;
  });
}

/**
 * O trang DAU TIEN mang mot trang goc cho truoc.
 *
 * Mot trang goc co the xuat hien NHIEU LAN trong so do (nhan doi la thao tac
 * hop le). Chu thich thi phai hien tren ca hai ban - do la nghia dung cua "chu
 * thich thuoc ve trang". Nhung o soan tai cho va o chu giai dang mo thi KHONG:
 * chung la mot phien lam viec duy nhat, va ve hai ban sao cua cung mot o soan
 * la mo hai cho go cho mot lan sua.
 */
export function firstSlotForOriginalPage(
  slots: readonly ViewSlot[],
  originalPageIndex: number,
): number | null {
  for (const slot of slots) {
    if (slot.originalPageIndex === originalPageIndex) return slot.slotIndex;
  }
  return null;
}

/** Trang goc do co con o trong so do khong. Rong nghia la da bi xoa het. */
export function planContainsOriginalPage(
  plan: PagePlan,
  originalPageIndex: number,
): boolean {
  return plan.some(
    (slot) => slot.docId === ORIGINAL_DOC_ID && slot.sourcePageIndex === originalPageIndex,
  );
}
